
import React, { useState, useEffect } from 'react';
import { Menu, X, Download, Bot, Newspaper, Search, MessageSquare, Info, Home, ArrowRight, Package, User as UserIcon, Instagram, Youtube, Facebook, Zap, Linkedin, Plus, ChevronDown, Layout, MapPin, Briefcase, ShoppingCart, Store, Trash2, Minus, Send, Gift, LogOut } from 'lucide-react';
import { SiteSettings, NewsItem, User, Product, CustomPage } from '../types';
import AdvancedSearch from './AdvancedSearch';
import { getBasePath, getArticleUrl } from '../constants';

const XIcon = ({ size = 18, className = "" }: { size?: number, className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
  </svg>
);

const TikTokIcon = ({ size = 18, className = "" }: { size?: number, className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 0 1-5.201 1.743l-.002-.001.002.001a2.895 2.895 0 0 1 3.183-4.51v-3.5a6.329 6.329 0 0 0-5.394 10.692 6.33 6.33 0 0 0 10.857-4.424V8.687a8.182 8.182 0 0 0 4.773 1.526V6.79a4.831 4.831 0 0 1-1.003-.104z" />
  </svg>
);

interface HeaderProps {
  settings: SiteSettings;
  currentPage: string;
  onNavigate: (page: string) => void;
  products?: Product[];
  news?: NewsItem[];
  onOpenAuth?: () => void;
  currentUser?: User | null;
  customPages?: CustomPage[];
  isScrolled?: boolean;
  canInstallPwa?: boolean;
  onInstallPwa?: () => void;
  onOpenSearch?: () => void;
  onLogout?: () => void;
}

const Header: React.FC<HeaderProps> = ({ 
  settings, 
  currentPage, 
  onNavigate, 
  news = [], 
  onOpenAuth, 
  currentUser, 
  customPages = [],
  isScrolled: isScrolledProp,
  canInstallPwa,
  onInstallPwa,
  onOpenSearch,
  onLogout
}) => {
  const [isScrolledInternal, setIsScrolledInternal] = useState(false);
  const isScrolled = isScrolledProp !== undefined ? isScrolledProp : isScrolledInternal;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  interface UnifiedCartItem {
    id: string;
    title: string;
    description?: string;
    price: number;
    promo_price?: number | null;
    is_promo?: boolean | number;
    image_url?: string;
    quantity: number;
    businessId: string;
    businessName: string;
    businessWhatsapp: string;
  }

  const [unifiedCart, setUnifiedCart] = useState<UnifiedCartItem[]>([]);
  const [isHeaderCartOpen, setIsHeaderCartOpen] = useState(false);

  useEffect(() => {
    const syncCart = () => {
      const saved = localStorage.getItem('unified_directory_cart');
      if (saved) {
        try {
          setUnifiedCart(JSON.parse(saved));
        } catch (e) {
          console.error("Failed to parse cart in header", e);
        }
      } else {
        setUnifiedCart([]);
      }
    };
    syncCart();
    window.addEventListener('cart-updated', syncCart);
    window.addEventListener('storage', syncCart);
    return () => {
      window.removeEventListener('cart-updated', syncCart);
      window.removeEventListener('storage', syncCart);
    };
  }, []);

  const removeFromCart = (productId: string, businessId: string) => {
    const rawCart = localStorage.getItem('unified_directory_cart');
    let currentCart: UnifiedCartItem[] = [];
    if (rawCart) {
      try {
        currentCart = JSON.parse(rawCart);
      } catch (e) {
        console.error(e);
      }
    }
    const updated = currentCart.filter(item => !(item.id === productId && item.businessId === businessId));
    localStorage.setItem('unified_directory_cart', JSON.stringify(updated));
    window.dispatchEvent(new Event('cart-updated'));
  };

  const updateQuantity = (productId: string, businessId: string, delta: number) => {
    const rawCart = localStorage.getItem('unified_directory_cart');
    let currentCart: UnifiedCartItem[] = [];
    if (rawCart) {
      try {
        currentCart = JSON.parse(rawCart);
      } catch (e) {
        console.error(e);
      }
    }
    const updated = currentCart.map(item => {
      if (item.id === productId && item.businessId === businessId) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    });
    localStorage.setItem('unified_directory_cart', JSON.stringify(updated));
    window.dispatchEvent(new Event('cart-updated'));
  };

  const clearBusinessCart = (businessId: string) => {
    const rawCart = localStorage.getItem('unified_directory_cart');
    let currentCart: UnifiedCartItem[] = [];
    if (rawCart) {
      try {
        currentCart = JSON.parse(rawCart);
      } catch (e) {
        console.error(e);
      }
    }
    const updated = currentCart.filter(item => item.businessId !== businessId);
    localStorage.setItem('unified_directory_cart', JSON.stringify(updated));
    window.dispatchEvent(new Event('cart-updated'));
  };

  const cartTotal = React.useMemo(() => {
    return unifiedCart.reduce((total, item) => {
      const price = item.is_promo && item.promo_price ? item.promo_price : item.price;
      return total + (price * item.quantity);
    }, 0);
  }, [unifiedCart]);

  const cartCount = React.useMemo(() => {
    return unifiedCart.reduce((total, item) => total + item.quantity, 0);
  }, [unifiedCart]);

  const groupedCart = React.useMemo(() => {
    const groups: Record<string, { businessId: string, businessName: string, businessWhatsapp: string, items: UnifiedCartItem[], total: number }> = {};
    
    unifiedCart.forEach(item => {
      if (!groups[item.businessId]) {
        groups[item.businessId] = {
          businessId: item.businessId,
          businessName: item.businessName,
          businessWhatsapp: item.businessWhatsapp,
          items: [],
          total: 0
        };
      }
      groups[item.businessId].items.push(item);
      const price = item.is_promo && item.promo_price ? item.promo_price : item.price;
      groups[item.businessId].total += price * item.quantity;
    });

    return Object.values(groups);
  }, [unifiedCart]);

  const formatWhatsAppMessage = (group: { businessName: string, items: UnifiedCartItem[], total: number }) => {
    let message = `*Novo Pedido - ${group.businessName}*\n\n`;
    message += `--- Itens do Pedido ---\n`;
    
    group.items.forEach(item => {
      const price = item.is_promo && item.promo_price ? item.promo_price : item.price;
      message += `• ${item.quantity}x ${item.title} - R$ ${(price * item.quantity).toFixed(2)}\n`;
    });
    
    message += `\n*Total: R$ ${group.total.toFixed(2)}*\n\n`;
    message += `_Pedido realizado via Guia Comercial Digital_`;
    
    return encodeURIComponent(message);
  };

  const handleFinalizeOrder = (group: { businessId: string, businessName: string, businessWhatsapp: string, items: UnifiedCartItem[], total: number }) => {
    const whatsapp = group.businessWhatsapp?.replace(/\D/g, '');
    if (!whatsapp) {
      alert('Esta empresa não possui WhatsApp cadastrado.');
      return;
    }
    const message = formatWhatsAppMessage(group);
    window.open(`https://wa.me/${whatsapp}?text=${message}`, '_blank');
  };
  
  useEffect(() => {
    const handleScroll = () => setIsScrolledInternal(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMenuOpen]);

  const headerPages = customPages.filter(p => p.show_in_header);
  
  const baseLinks = [
    { id: 'home', label: 'Início', icon: Home },
    { id: 'quem-somos', label: 'Quem Somos', icon: Info },
    { id: 'produtos', label: 'Produtos', icon: Package },
    ...(settings.enableDirectory && !settings.directoryAsHomePage ? [{ id: 'guia', label: 'Guia', icon: MapPin }] : []),
    { id: 'blog', label: 'Blog', icon: Newspaper },
    ...(settings.gamificationEnabled ? [{ id: 'beneficios', label: 'Benefícios', icon: Gift }] : []),
    { id: 'contato', label: 'Contato', icon: MessageSquare },
  ];

  const allLinks: Array<{ id: string, label: string, icon: any, isCustom?: boolean }> = [];
  
  // Position custom pages after their explicitly targeted menu links
  baseLinks.forEach(baseLink => {
    allLinks.push(baseLink);
    const targetingThis = headerPages.filter(p => p.position_header_target === baseLink.id);
    targetingThis.forEach(p => {
      allLinks.push({
        id: `p/${p.slug}`,
        label: p.title,
        icon: Layout,
        isCustom: true
      });
    });
  });

  // Handle pages with no target, placing them in their original spot after products/directory with overflow protection
  const unmatched = headerPages.filter(p => {
    if (!p.position_header_target) return true;
    return !baseLinks.some(bl => bl.id === p.position_header_target);
  });

  unmatched.forEach(p => {
    const insertAfterId = settings.enableDirectory && !settings.directoryAsHomePage ? 'guia' : 'produtos';
    const index = allLinks.findIndex(link => link.id === insertAfterId);
    if (index !== -1) {
      allLinks.splice(index + 1, 0, {
        id: `p/${p.slug}`,
        label: p.title,
        icon: Layout,
        isCustom: true
      });
    } else {
      allLinks.push({
        id: `p/${p.slug}`,
        label: p.title,
        icon: Layout,
        isCustom: true
      });
    }
  });

  const handleLinkClick = (id: string) => {
    onNavigate(id);
    setIsMenuOpen(false);
    setIsMoreOpen(false);
  };

  // Safe client-side dynamic overflow calculation
  const navRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(allLinks.length);

  useEffect(() => {
    const handleResize = () => {
      if (!navRef.current || !measureRef.current) return;
      
      const containerWidth = navRef.current.getBoundingClientRect().width;
      const measureChildren = Array.from(measureRef.current.children) as HTMLElement[];
      
      if (measureChildren.length === 0) return;

      const moreBtnWidth = 90; // Approx space for "Mais" drop-down button
      let currentWidth = 0;
      let fitCount = allLinks.length;
      let hasOverflow = false;

      // Check if total items width exceeds container width
      for (let i = 0; i < measureChildren.length; i++) {
        const itemWidth = measureChildren[i].getBoundingClientRect().width;
        const itemSpacing = i > 0 ? 32 : 0; // gap-8 = 32px
        currentWidth += itemWidth + itemSpacing;
      }

      if (currentWidth > containerWidth) {
        hasOverflow = true;
        currentWidth = 0;
        
        for (let i = 0; i < measureChildren.length; i++) {
          const itemWidth = measureChildren[i].getBoundingClientRect().width;
          const itemSpacing = i > 0 ? 32 : 0;
          
          if (currentWidth + itemWidth + itemSpacing + moreBtnWidth > containerWidth) {
            fitCount = i;
            break;
          }
          currentWidth += itemWidth + itemSpacing;
        }
      }

      setVisibleCount(hasOverflow ? Math.max(1, fitCount) : allLinks.length);
    };

    // Calculate once on mount/update
    const timer = setTimeout(handleResize, 100);
    
    let resizeObserver: ResizeObserver | null = null;
    if (navRef.current && typeof window !== 'undefined' && 'ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        handleResize();
      });
      resizeObserver.observe(navRef.current);
    } else {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      clearTimeout(timer);
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [allLinks]);

  const visibleLinks = allLinks.slice(0, visibleCount);
  const hiddenLinks = allLinks.slice(visibleCount);

  const isHeroPage = currentPage === 'home' || currentPage === 'guia';

  return (
    <header 
      className={`transition-all duration-500 ease-in-out ${
        isMenuOpen
          ? 'fixed top-0 left-0 right-0 w-full bg-transparent border-b-0 py-0 z-[1000]'
          : isHeroPage 
            ? isScrolled
              ? 'fixed top-0 left-0 right-0 w-full bg-white border-b border-slate-200/50 shadow-md py-3 z-[1000]'
              : 'absolute top-0 left-0 right-0 w-full bg-white border-b border-gray-100 py-4 md:py-5 z-[1000]'
            : isScrolled
              ? 'sticky top-0 left-0 right-0 w-full bg-white border-b border-gray-200 shadow-md py-3.5 z-[1000]'
              : 'relative bg-white border-b border-gray-100 py-5 z-[500]'
      }`}
    >
      {/* Top Accent Line (Strokes/Lines detail requested by user) */}
      <div 
        className="absolute top-0 left-0 right-0 h-[3px] z-[1002]"
        style={{ 
          backgroundColor: settings.primaryColor || '#2563eb'
        }}
      />

      {/* Off-screen Measurement Container */}
      <div 
        ref={measureRef} 
        className="absolute top-0 left-0 opacity-0 pointer-events-none select-none flex items-center gap-8 whitespace-nowrap overflow-hidden text-[12px] font-bold uppercase tracking-wider"
        style={{ width: '5000px', zIndex: -9999 }}
      >
        {allLinks.map((link) => (
          <span key={link.id} className="py-2 inline-flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 block shrink-0"></span>
            {link.label}
          </span>
        ))}
      </div>

      {/* DESKTOP CONTAINER */}
      <div className="hidden lg:flex max-w-[1600px] mx-auto px-4 md:px-8 items-center justify-between gap-4">
        
        {/* LOGO */}
        <div 
          className="flex items-center gap-2 xs:gap-3 cursor-pointer group z-[1001] min-w-0 shrink-1"
          onClick={() => { onNavigate('home'); setIsMenuOpen(false); }}
        >
          {settings.logoUrl ? (
            <img 
              src={settings.logoUrl} 
              alt={settings.siteTitle || "Logo"} 
              className="h-10 md:h-12 w-auto object-contain group-hover:scale-105 transition-transform shrink-0" 
            />
          ) : settings.siteTitle ? (
            <>
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center text-white shadow-lg group-hover:scale-105 transition-transform shrink-0">
                <Bot size={22} />
              </div>
              <span className="text-base xs:text-lg sm:text-xl font-bold tracking-tight text-gray-900 uppercase truncate max-w-[110px] xs:max-w-[170px] sm:max-w-none">
                {settings?.siteTitle ? (
                  <>
                    {settings.siteTitle.split(' ')[0]}<span className="text-primary">{settings.siteTitle.split(' ').slice(1).join(' ')}</span>
                  </>
                ) : ''}
              </span>
            </>
          ) : (
            <>
              <div className="w-10 h-10 bg-slate-200 rounded-lg animate-pulse shrink-0"></div>
              <div className="h-6 w-32 bg-slate-200 rounded-md animate-pulse"></div>
            </>
          )}
        </div>

        {/* DESKTOP NAV WITH AUTO OVERFLOW AND MEGA DROPDOWN */}
        <nav ref={navRef} className="hidden lg:flex items-center gap-8 flex-1 justify-center min-w-0 max-w-[1150px]">
          {visibleLinks.map((link) => {
            const isActive = currentPage === link.id || (link.id === 'home' && currentPage === 'guia' && settings.directoryAsHomePage);
            return (
              <button
                key={link.id}
                onClick={() => handleLinkClick(link.id)}
                className={`relative py-2 text-[12px] font-bold uppercase tracking-wider transition-all whitespace-nowrap flex-shrink-0 flex items-center gap-1.5 group ${isActive ? 'text-primary' : 'text-gray-500 hover:text-primary'}`}
              >
                <link.icon size={14} className={`transition-colors duration-300 ${isActive ? 'text-primary animate-pulse' : 'text-slate-400 group-hover:text-primary'}`} />
                <span>{link.label}</span>
                {isActive && <span className="absolute -bottom-1 left-0 w-full h-[2px] bg-primary rounded-full animate-fade-in"></span>}
              </button>
            );
          })}

          {hiddenLinks.length > 0 && (
            <div className="relative">
              <button 
                onClick={() => setIsMoreOpen(!isMoreOpen)}
                className="flex items-center gap-1.5 py-2 text-[12px] font-bold uppercase tracking-wider text-gray-600 hover:text-primary transition-all whitespace-nowrap cursor-pointer hover:underline"
              >
                mais <ChevronDown size={14} className={`text-primary transition-transform duration-300 ${isMoreOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isMoreOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsMoreOpen(false)}></div>
                  <div className="absolute top-full right-0 mt-2.5 w-52 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-100 py-3 z-50 animate-fade-in-up">
                    <div className="px-5 pb-2 mb-2 border-b border-gray-100/60 flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-slate-400 select-none">
                      <span>Mais Opções</span>
                      <Plus size={11} className="text-primary" />
                    </div>
                    {hiddenLinks.map((link) => {
                      const isActive = currentPage === link.id || (link.id === 'home' && currentPage === 'guia' && settings.directoryAsHomePage);
                      return (
                        <button
                          key={link.id}
                          onClick={() => handleLinkClick(link.id)}
                          className={`w-full text-left px-5 py-2.5 text-[11px] font-bold uppercase tracking-wider transition-all flex items-center gap-2.5 ${isActive ? 'text-primary bg-indigo-50/40' : 'text-gray-600 hover:bg-gray-50 hover:text-primary'}`}
                        >
                          <link.icon size={13} className={isActive ? 'text-primary' : 'text-slate-400'} />
                          {link.label}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </nav>

        {/* ACTIONS */}
        <div className="flex items-center gap-1.5 xs:gap-3 md:gap-5 z-[1001] shrink-0">
          {/* SOCIAL ICONS DESKTOP */}
          <div className="hidden lg:flex items-center gap-2 mr-2 border-r border-gray-100 pr-4">
            {settings.instagramUrl && <a href={settings.instagramUrl} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-pink-600 transition-colors"><Instagram size={18} /></a>}
            {settings.facebookUrl && <a href={settings.facebookUrl} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-blue-600 transition-colors"><Facebook size={18} /></a>}
            {settings.linkedInUrl && <a href={settings.linkedInUrl} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-blue-700 transition-colors"><Linkedin size={18} /></a>}
            {settings.youtubeUrl && <a href={settings.youtubeUrl} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-red-600 transition-colors"><Youtube size={18} /></a>}
            {settings.tiktokUrl && <a href={settings.tiktokUrl} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-black transition-colors"><TikTokIcon size={18} /></a>}
            {settings.xUrl && <a href={settings.xUrl} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-black transition-colors"><XIcon size={16} /></a>}
          </div>

          <button onClick={() => onOpenSearch ? onOpenSearch() : setIsSearchModalOpen(true)} className="p-2 text-gray-400 hover:text-primary transition-colors shrink-0" aria-label="Buscar"><Search size={20} /></button>
          
          {/* Global Cart Button */}
          {cartCount > 0 && (
            <button 
              onClick={() => setIsHeaderCartOpen(true)} 
              className="p-2 text-orange-600 hover:text-orange-700 bg-orange-50 hover:bg-orange-100/80 transition-all rounded-xl flex items-center gap-1.5 px-3 py-2 border border-orange-200/35 relative active:scale-95 shrink-0"
              title="Meu Carrinho"
            >
              <div className="relative">
                <ShoppingCart size={18} />
                <span className="absolute -top-2 -right-2 bg-orange-600 text-white font-extrabold text-[8px] w-4.5 h-4.5 rounded-full flex items-center justify-center shadow-sm">
                  {cartCount}
                </span>
              </div>
              <span className="hidden sm:inline text-[10px] font-black uppercase tracking-wider">R$ {cartTotal.toFixed(2)}</span>
            </button>
          )}
          
          {canInstallPwa && (
            <>
              {/* Desktop Install Button */}
              <button 
                id="header-pwa-install-btn-desktop"
                onClick={onInstallPwa}
                className="hidden md:flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-[11px] font-black uppercase tracking-wider transition-all shadow-md active:scale-95 hover:scale-[1.02] cursor-pointer shrink-0"
              >
                <Download size={14} className="animate-bounce" /> APP
              </button>
              {/* Mobile/Tablet Install Icon Button */}
              <button 
                id="header-pwa-install-btn-mobile"
                onClick={onInstallPwa}
                className={`items-center justify-center p-2.5 text-indigo-600 bg-indigo-50 hover:bg-indigo-100/80 transition-all rounded-xl border border-indigo-200/30 active:scale-95 cursor-pointer shrink-0 ${cartCount > 0 ? 'hidden sm:flex' : 'flex'} md:hidden`}
                title="Instalar Aplicativo Nativo"
              >
                <Download size={18} className="animate-bounce" />
              </button>
            </>
          )}

          <button 
            onClick={() => {
              if (currentUser) {
                onNavigate('admin');
              } else {
                onOpenAuth?.();
              }
            }} 
            className="hidden sm:block bg-gray-900 hover:bg-primary text-white px-6 py-2.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all shadow-lg active:scale-95 shrink-0"
          >
            {currentUser ? `Olá, ${(currentUser.name || 'Usuário').split(' ')[0]}` : 'Entrar'}
          </button>

          {currentUser && (
            <button
              onClick={() => onLogout?.()}
              title="Sair da Conta"
              className="hidden sm:flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-600 p-2.5 rounded-lg border border-red-100 transition-all shadow-sm active:scale-95 shrink-0"
            >
              <LogOut size={16} />
            </button>
          )}

          {/* HAMBURGER */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
            className={`lg:hidden p-2.5 rounded-lg transition-all border shadow-sm shrink-0 ${
              isMenuOpen ? 'bg-primary text-white border-primary' : 'bg-gray-50 text-gray-900 border-gray-100'
            }`}
          >
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* MOBILE CONTAINER */}
      <div className={`flex lg:hidden w-full px-4 items-center justify-between relative z-[1001] transition-all duration-300 ${
        isMenuOpen ? 'bg-white border-b border-gray-100/85 shadow-sm py-2.5' : ''
      }`}>
        <div className="grid grid-cols-3 w-full items-center">
          {/* Left: Hamburger menu */}
          <div className="flex items-center justify-start">
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
              className="p-2 text-gray-700 hover:text-primary transition-colors shrink-0"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Center: Logo */}
          <div 
            className="flex items-center justify-center cursor-pointer group z-[1001]"
            onClick={() => { onNavigate('home'); setIsMenuOpen(false); }}
          >
            {settings.logoUrl ? (
              <img 
                src={settings.logoUrl} 
                alt={settings.siteTitle || "Logo"} 
                className="h-8 xs:h-9 w-auto object-contain transition-transform" 
              />
            ) : settings.siteTitle ? (
              <span className="text-sm xs:text-base font-black tracking-tight text-gray-900 uppercase text-center whitespace-nowrap">
                {settings.siteTitle.split(' ')[0]}<span className="text-primary">{settings.siteTitle.split(' ').slice(1).join(' ')}</span>
              </span>
            ) : (
              <div className="h-5 w-24 bg-gray-200 dark:bg-gray-800 rounded animate-pulse"></div>
            )}
          </div>

          {/* Right: Search & Other active icons */}
          <div className="flex items-center justify-end gap-1.5 xs:gap-2">
            {cartCount > 0 && (
              <button 
                onClick={() => setIsHeaderCartOpen(true)} 
                className="p-1.5 text-orange-600 hover:text-orange-700 bg-orange-50 hover:bg-orange-100 transition-all rounded-lg flex items-center relative active:scale-95 shrink-0"
                title="Meu Carrinho"
              >
                <ShoppingCart size={16} />
                <span className="absolute -top-1.5 -right-1.5 bg-orange-600 text-white font-extrabold text-[8px] w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center shadow-sm">
                  {cartCount}
                </span>
              </button>
            )}

            <button 
              onClick={() => onOpenSearch ? onOpenSearch() : setIsSearchModalOpen(true)} 
              className="p-2 text-gray-500 hover:text-primary transition-colors shrink-0" 
              aria-label="Buscar"
            >
              <Search size={22} />
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE OVERLAY */}
      <div className={`lg:hidden fixed inset-0 bg-[#f8fafc] z-[999] transition-all duration-500 ease-in-out transform ${
        isMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
      }`}>
        <div className="h-full flex flex-col pt-24 px-4 pb-8 overflow-y-auto">
          <div className="max-w-md mx-auto w-full space-y-4">
            
            {/* WELCOME CARD */}
            <div className="flex flex-col gap-3 bg-white p-4 rounded-2xl border border-gray-100 shadow-xl shadow-gray-100/40">
              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0">
                  <UserIcon size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base font-bold text-gray-900 uppercase tracking-tight leading-none mb-1 truncate">
                    {currentUser ? `Olá, ${(currentUser.name || 'Usuário').split(' ')[0]}!` : 'Bem-vindo!'}
                  </h3>
                  <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Plataforma de Automação</p>
                </div>
                {currentUser && (
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      onLogout?.();
                    }}
                    title="Sair"
                    className="p-2 rounded-lg bg-red-50 text-red-600 border border-red-100 hover:bg-red-100 transition-all active:scale-95 shrink-0"
                  >
                    <LogOut size={16} />
                  </button>
                )}
              </div>
              {canInstallPwa && (
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    onInstallPwa?.();
                  }}
                  className="w-full mt-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all shadow-md active:scale-95 animate-pulse"
                >
                  <Download size={14} /> Instalar Aplicativo
                </button>
              )}
            </div>

            {/* GRID MENU */}
            <div className="grid grid-cols-2 gap-2.5">
              {allLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => handleLinkClick(link.id)}
                  className={`flex flex-row items-center gap-2 px-3 py-2.5 rounded-xl transition-all duration-300 border-2 ${
                    currentPage === link.id 
                    ? 'bg-primary border-primary text-white shadow-md shadow-primary/25 scale-[1.01]' 
                    : 'bg-white border-transparent text-gray-700 hover:border-gray-100 hover:bg-gray-50 shadow-sm'
                  }`}
                >
                  <div className={`p-1.5 rounded-lg shrink-0 ${
                    currentPage === link.id ? 'bg-white/20' : 'bg-slate-100 text-slate-500'
                  }`}>
                    <link.icon size={16} />
                  </div>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-left truncate">{link.label}</span>
                </button>
              ))}
              
              {/* PAINEL BUTTON */}
              <button 
                onClick={() => { 
                  if (currentUser) {
                    onNavigate('admin');
                  } else {
                    onOpenAuth?.(); 
                  }
                  setIsMenuOpen(false); 
                }} 
                className="flex flex-row items-center gap-2 px-3 py-2.5 rounded-xl bg-slate-900 text-white border-2 border-slate-900 shadow-md shadow-slate-900/10"
              >
                <div className="p-1.5 rounded-lg bg-white/10 text-primary shrink-0">
                  <Zap size={16} fill="currentColor" />
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-left">Painel</span>
              </button>

              {canInstallPwa && (
                <button 
                  onClick={() => {
                    onInstallPwa?.();
                    setIsMenuOpen(false);
                  }} 
                  className="col-span-2 flex items-center justify-center gap-2 py-3 px-4 rounded-2xl bg-blue-600 text-white font-bold transition-all shadow-xl shadow-blue-500/20 active:scale-95"
                >
                  <Download size={16} />
                  <span className="text-[11px] uppercase tracking-wider">Baixar Aplicativo Nativo</span>
                </button>
              )}
            </div>

          </div>
        </div>
      </div>

      {isSearchModalOpen && (
        <AdvancedSearch 
          items={news} categories={[]} isOpen={isSearchModalOpen} 
          onClose={() => setIsSearchModalOpen(false)} 
          onNavigate={(article) => { 
            setIsSearchModalOpen(false); 
            window.location.href = getArticleUrl(article.slug || article.id, article.category); 
          }} 
        />
      )}

      {isHeaderCartOpen && (
        <div className="fixed inset-0 z-[1200] flex items-end md:items-center justify-center p-0 md:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300 pointer-events-auto">
          <div className="bg-white w-full max-w-lg rounded-t-[32px] md:rounded-[40px] shadow-2xl overflow-hidden animate-in slide-in-from-bottom-10 md:zoom-in-95 duration-300 max-h-[90vh] flex flex-col pointer-events-auto">
            <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
              <div className="flex items-center gap-3">
                <ShoppingCart className="text-orange-600" size={24} />
                <div>
                  <h3 className="text-md md:text-lg font-black text-gray-900 uppercase tracking-tight">Carrinho Inteligente</h3>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Multi-Empresas do Guia</p>
                </div>
              </div>
              <button onClick={() => setIsHeaderCartOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              {groupedCart.map(group => (
                <div key={group.businessId} className="bg-gray-50/50 rounded-[28px] p-4 md:p-5 border border-gray-100/80 space-y-4">
                  {/* Business Group Header */}
                  <div className="flex items-center justify-between pb-3 border-b border-gray-200/60">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-orange-500/10 text-orange-600 flex items-center justify-center shadow-inner">
                        <Store size={16} />
                      </div>
                      <span className="font-extrabold text-sm text-gray-900 uppercase tracking-wide truncate max-w-[220px]">
                        {group.businessName}
                      </span>
                    </div>
                    <button 
                      onClick={() => clearBusinessCart(group.businessId)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      title="Esvaziar pedido deste estabelecimento"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {/* Grouped Items List */}
                  <div className="space-y-3">
                    {group.items.map(item => {
                      const price = item.is_promo && item.promo_price ? item.promo_price : item.price;
                      return (
                        <div key={item.id} className="flex gap-4 p-3 bg-white rounded-2xl border border-gray-100 group">
                          <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-50 border border-gray-100/50 shrink-0">
                            <img src={item.image_url || 'https://picsum.photos/seed/food/100/100'} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                          </div>
                          <div className="flex-1 min-w-0 flex flex-col justify-between">
                            <div className="flex justify-between items-start gap-2">
                              <h5 className="font-bold text-gray-900 text-xs truncate">{item.title}</h5>
                              <button onClick={() => removeFromCart(item.id, group.businessId)} className="text-gray-400 hover:text-red-500 transition-colors shrink-0">
                                <X size={14} />
                              </button>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-bold text-orange-600">R$ {price.toFixed(2)}</p>
                              <div className="flex items-center gap-2.5 bg-gray-50 border border-gray-200/50 rounded-lg px-2 py-0.5">
                                <button onClick={() => updateQuantity(item.id, group.businessId, -1)} className="p-0.5 text-gray-400 hover:text-orange-600 transition-colors">
                                  <Minus size={12} />
                                </button>
                                <span className="text-xs font-black text-gray-900 w-3 text-center">{item.quantity}</span>
                                <button onClick={() => updateQuantity(item.id, group.businessId, 1)} className="p-0.5 text-gray-400 hover:text-orange-600 transition-colors">
                                  <Plus size={12} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Checkout Actions for this Business */}
                  <div className="pt-3 border-t border-dashed border-gray-200/60 flex flex-col gap-3">
                    <div className="flex items-center justify-between text-xs font-bold text-gray-500 uppercase tracking-widest px-1">
                      <span>Subtotal {group.businessName}</span>
                      <span className="text-sm font-black text-gray-900">R$ {group.total.toFixed(2)}</span>
                    </div>
                    <button 
                      onClick={() => handleFinalizeOrder(group)}
                      className="w-full bg-emerald-500 text-white py-3 px-4 rounded-xl font-bold uppercase tracking-widest text-[10px] sm:text-xs shadow-md shadow-emerald-500/10 hover:bg-emerald-600 transition-all active:scale-95 flex items-center justify-center gap-2"
                    >
                      <Send size={14} />
                      <span>Enviar Pedido WhatsApp</span>
                    </button>
                  </div>
                </div>
              ))}

              {unifiedCart.length === 0 && (
                <div className="text-center py-20">
                  <ShoppingCart className="mx-auto text-gray-200 mb-4" size={48} />
                  <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">Seu carrinho está vazio</p>
                  <p className="text-[10px] text-gray-400 mt-2">Navegue pelas empresas e adicione produtos ao pedido!</p>
                </div>
              )}
            </div>

            {/* Global Sticky Footer */}
            {unifiedCart.length > 0 && (
              <div className="p-6 bg-slate-950 border-t border-slate-900 text-white space-y-1 rounded-t-[24px]">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px]">Soma de Todos os Pedidos</span>
                    <span className="text-[10px] text-emerald-400 font-medium font-semibold">Os pedidos são enviados separadamente por estabelecimento</span>
                  </div>
                  <span className="text-xl font-black text-white">R$ {cartTotal.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
